import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useState } from "react";

import { PublicationRead, PublicationVersionRead } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";

import { useFileDescriptorQueryDirectly } from "@/queries/query-file-descriptors";
import { usePublicationVersionFilesQueryDirectly } from "@/queries/query-publication-version-files";
import {
  getPublicationVersionQuery,
  usePublicationVersionQueryDirectly,
} from "@/queries/query-version";

export const useSetVersion = (publicationRead: PublicationRead) => {
  const { isPublic } = useContext(TenantContext);
  const initialVersion = isPublic ? "published" : "latest";
  const [version, _setVersion] = useState<number | "latest" | "published">(initialVersion);
  const [latestVersion, setLatestVersion] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const fetchFileDescriptor = useFileDescriptorQueryDirectly();
  const fetchVersionFiles = usePublicationVersionFilesQueryDirectly();
  const fetchPublicationVersion = usePublicationVersionQueryDirectly();
  const setPublicationVersion = useCallback(
    async (versionOrRead: PublicationVersionRead | number) => {
      if (isPublic) {
        console.warn("Switching versions is not supported in the public tenant.");
        return;
      }

      // 1. Fetch versionRead if we only know the version number
      const versionRead =
        typeof versionOrRead === "number"
          ? (
              await fetchPublicationVersion(
                publicationRead.id,
                publicationRead.publishedVersion ?? undefined,
                versionOrRead
              )
            ).data
          : versionOrRead;

      if (!versionRead) return;

      const version = versionRead.version;
      const isLatest = version > (latestVersion ?? 0);

      // 2. Fetch the new version's files and populate the files query cache
      const fileFetchReturn = versionRead.files?.length
        ? await fetchVersionFiles(publicationRead.id, version)
        : null;

      if (!fileFetchReturn || fileFetchReturn.isError) return;

      // 3. Populate each descriptor's individual cache entry
      await Promise.all(
        fileFetchReturn.data.map((file) => fetchFileDescriptor(file.file.descriptorId))
      );

      const setVersionReadFor = (version: number | "latest" | "published") => {
        queryClient.setQueryData<PublicationVersionRead>(
          getPublicationVersionQuery(
            publicationRead.id,
            publicationRead.publishedVersion ?? undefined,
            version
          ).queryKey,
          versionRead
        );
      };

      // 4. Set the version query cache for this version
      setVersionReadFor(version);

      // 5. If this is the latest version, we also set the "latest" query data, so that when the user switches to "latest", it is already populated
      if (isLatest) {
        setVersionReadFor("latest");
      }

      // 6. AFTER all the fetches, Set the current version in the store, so the UI switches to it.
      _setVersion(version);
      if (isLatest) {
        setLatestVersion(version);
      }
    },
    [
      isPublic,
      fetchPublicationVersion,
      publicationRead.id,
      publicationRead.publishedVersion,
      latestVersion,
      fetchVersionFiles,
      fetchFileDescriptor,
      queryClient,
    ]
  );

  return { version, setPublicationVersion, latestVersion, setLatestVersion };
};
